import { type ConfirmOptions, useConfirmModalStore } from '@client/stores/confirmModalStore';
import { App } from 'antd';
import { CircleAlert } from 'lucide-react';

let modal: ReturnType<typeof App.useApp>['modal'];

export function useInitModal() {
    modal = App.useApp().modal;
}

/**
 * Ant's imperative modal API (`modal.info`, `modal.warning`, `modal.error`, `modal.success`).
 * Confirmations do not go through `modal.confirm`: use `confirmAction` or `confirmDestructive`
 * below, which render ButtonModern buttons and resolve to the user's answer (ratchet AC2).
 */
export { modal };

export type ConfirmActionOptions = Omit<ConfirmOptions, 'okDanger' | 'onReplaced'>;

function ask(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
        let running = false;
        let replaced = false;
        const id = useConfirmModalStore.getState().show({
            ...options,
            onOk: async () => {
                // A rejected `onOk` keeps the modal open for a retry (see `ConfirmModal`), so the
                // promise settles only once the action succeeds or the user cancels. This request
                // is closed before the answer resolves, so a caller that awaits and immediately asks
                // again opens a fresh dialog instead of one this handler would close.
                running = true;
                try {
                    await options.onOk?.();
                } catch (error) {
                    // The dialog is gone when a follow-up replaced it, so there is nothing to retry:
                    // settle as "not done" instead of leaving the caller waiting.
                    if (replaced) resolve(false);
                    throw error;
                } finally {
                    running = false;
                }
                useConfirmModalStore.getState().hide(id);
                resolve(true);
            },
            onCancel: () => {
                options.onCancel?.();
                resolve(false);
            },
            // Another confirmation took the slot. A dialog whose `onOk` is running (it opened a
            // follow-up) still gets its `true`; an unanswered one resolves `false` without
            // running `onCancel`, since the user never chose.
            onReplaced: () => {
                replaced = true;
                if (!running) resolve(false);
            },
        });
    });
}

/**
 * Asks before an action the user may not intend but can recover from (publish, reassign,
 * change a setting). Primary OK, secondary Cancel. Resolves `true` once OK (and `onOk`, when
 * given) completes and `false` on Cancel, Escape or the mask.
 *
 * @example
 * if (await confirmAction({ title: 'Publish this version?', okText: 'Publish' })) publish();
 */
export function confirmAction(options: ConfirmActionOptions): Promise<boolean> {
    return ask(options);
}

/**
 * Asks before something is deleted, discarded, revoked or otherwise hard to get back.
 * Danger-filled OK (default "Delete"), secondary Cancel. Same result contract as
 * `confirmAction`. A deletion that must be typed out to confirm uses `useDeleteConfirmation`;
 * one that needs a stored reason uses `DeleteWithReasonModal`.
 *
 * @example
 * confirmDestructive({
 *     title: 'Delete this template?',
 *     content: 'The template is removed from every organisation that uses it.',
 *     onOk: () => deleteTemplate.mutateAsync({ id }),
 * });
 */
export function confirmDestructive({ okText = 'Delete', icon, ...options }: ConfirmActionOptions): Promise<boolean> {
    return ask({
        ...options,
        okText,
        okDanger: true,
        icon: icon ?? <CircleAlert className="text-status-danger" size={20} />,
    });
}
